<template>
    <div>
        <pa-section title="Versões do plano" description="A versão publicada é a que os credores leem antes de aderir." flush>
            <template #actions>
                <v-btn color="primary" depressed @click="openUpload"><v-icon left small>mdi-upload</v-icon>Enviar nova versão</v-btn>
            </template>
            <v-data-table :headers="headers" :items="documents" hide-default-footer disable-pagination disable-sort
                no-data-text="Nenhuma versão do plano enviada">
                <template v-slot:[`item.version`]="{ item }"><span class="font-weight-medium">v{{ item.version }}</span></template>
                <template v-slot:[`item.title`]="{ item }">
                    <div class="font-weight-medium">{{ item.title }}</div>
                    <div class="pa-small pa-muted">{{ item.file_name }} · {{ formatBytes(item.file_size) }} · <span class="pa-mono" :title="item.file_hash">{{ String(item.file_hash || "").slice(0, 12) }}</span></div>
                </template>
                <template v-slot:[`item.status`]="{ item }">
                    <pa-badge :status="item.status" />
                    <div v-if="item.status === 'rejected' && item.rejection_reason" class="pa-small mt-1" style="color: #b91c1c;">{{ item.rejection_reason }}</div>
                </template>
                <template v-slot:[`item.author`]="{ item }">
                    <div>{{ authorLabels[item.uploaded_by_type] || item.uploaded_by_type }}</div>
                    <div class="pa-small pa-muted">{{ formatDateTime(item.created_at) }}</div>
                </template>
                <template v-slot:[`item.actions`]="{ item }">
                    <div class="d-flex justify-end align-center" style="gap: 6px;">
                        <template v-if="canReview && item.status === 'pending_approval'">
                            <v-btn small outlined class="pa-btn-danger" @click="openReject(item)">Rejeitar</v-btn>
                            <v-btn small color="primary" depressed :loading="busy === `approve-${item.id}`" @click="act(item, 'approve', { publish: true })">Aprovar e publicar</v-btn>
                        </template>
                        <v-btn v-if="canReview && item.status === 'approved'" small color="primary" depressed
                            :loading="busy === `publish-${item.id}`" @click="act(item, 'publish')">Publicar</v-btn>
                        <v-btn small icon title="Abrir PDF" :loading="busy === `view-${item.id}`" @click="view(item)"><v-icon small>mdi-open-in-new</v-icon></v-btn>
                    </div>
                </template>
            </v-data-table>
        </pa-section>

        <v-dialog v-model="upload.open" max-width="520" :persistent="upload.loading" content-class="pa-ui-overlay">
            <v-card class="pa-card">
                <div class="pa-card__header">
                    <div>
                        <h3 class="pa-section-title">Enviar nova versão do plano</h3>
                        <p class="pa-section-description">{{ uploadHint }}</p>
                    </div>
                </div>
                <div class="pa-card__body">
                    <v-text-field v-model="upload.title" label="Título" outlined dense hide-details maxlength="255" class="mb-4"
                        placeholder="Plano de Recuperação Judicial"></v-text-field>
                    <v-file-input v-model="upload.file" label="PDF do plano (até 100 MB)" accept="application/pdf,.pdf" outlined dense
                        hide-details show-size prepend-icon="" prepend-inner-icon="mdi-file-pdf-box"></v-file-input>
                    <v-progress-linear v-if="upload.loading && upload.progress !== null" :value="upload.progress" class="mt-4" height="6" rounded color="primary"></v-progress-linear>
                </div>
                <div class="pa-card__footer">
                    <v-btn outlined :disabled="upload.loading" @click="upload.open = false">Cancelar</v-btn>
                    <v-btn color="primary" depressed :loading="upload.loading" :disabled="!upload.file" @click="send">Enviar</v-btn>
                </div>
            </v-card>
        </v-dialog>

        <v-dialog v-model="reject.open" max-width="520" content-class="pa-ui-overlay">
            <v-card class="pa-card">
                <div class="pa-card__header">
                    <div>
                        <h3 class="pa-section-title">Rejeitar versão do plano</h3>
                        <p class="pa-section-description">O motivo é enviado à recuperanda.</p>
                    </div>
                </div>
                <div class="pa-card__body">
                    <v-textarea v-model="reject.reason" label="Motivo" outlined rows="3" auto-grow maxlength="1000" counter hide-details="auto"></v-textarea>
                </div>
                <div class="pa-card__footer">
                    <v-btn outlined @click="reject.open = false">Cancelar</v-btn>
                    <v-btn color="error" depressed :disabled="!reject.reason.trim()" :loading="busy === 'reject'" @click="confirmReject">Rejeitar</v-btn>
                </div>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import axios from "axios";
import { chunkedUpload } from "@/common/util/chunked-upload";
import { AUTHOR_LABELS, errorMessage, formatDateTime, formatBytes } from "./plan-adhesion.util";
import PaBadge from "./PaBadge.vue";
import PaSection from "./PaSection.vue";

// Acima disso o arquivo vai em partes pela API (o multipart aceita ate 20 MB).
const MULTIPART_LIMIT = 19 * 1024 * 1024;
const MAX_BYTES = 100 * 1024 * 1024;

// Versoes do plano de recuperacao judicial. AJ (canReview) aprova, rejeita e
// publica o que a recuperanda envia; o que o AJ envia ja sai publicado.
export default {
    name: "PlanAdhesionPlanDocuments",

    components: { PaBadge, PaSection },

    props: {
        documents: { type: Array, default: () => [] },
        uploadUrl: { type: String, required: true },
        fileUrl: { type: Function, required: true },
        actionUrl: { type: Function, default: null },
        canReview: { type: Boolean, default: false },
        uploadHint: { type: String, default: "" },
    },

    data: () => ({
        upload: { open: false, title: "", file: null, loading: false, progress: null },
        reject: { open: false, reason: "", item: null },
        busy: null,
        authorLabels: AUTHOR_LABELS,
        headers: [
            { text: "Versão", value: "version", width: 70 },
            { text: "Plano", value: "title" },
            { text: "Status", value: "status" },
            { text: "Enviado por", value: "author" },
            { text: "", value: "actions", align: "end", width: 300 },
        ],
    }),

    methods: {
        formatDateTime,
        formatBytes,

        notifyError(error) {
            this.$dialog.notify.error(errorMessage(error), { position: "top-right", timeout: 4000 });
        },

        openUpload() {
            this.upload = { open: true, title: "", file: null, loading: false, progress: null };
        },

        async send() {
            const file = this.upload.file;
            if (!file) return;
            if (!/\.pdf$/i.test(file.name)) return this.$dialog.notify.error("Envie um arquivo PDF.", { position: "top-right", timeout: 3000 });
            if (file.size > MAX_BYTES) return this.$dialog.notify.error("O plano deve ter no máximo 100 MB.", { position: "top-right", timeout: 3000 });

            this.upload.loading = true;
            this.upload.progress = null;
            try {
                if (file.size > MULTIPART_LIMIT) {
                    this.upload.progress = 0;
                    const stored = await chunkedUpload(file, { onProgress: (value) => { this.upload.progress = value; } });
                    await axios.post(this.uploadUrl, { title: this.upload.title, uploaded_key: stored.key, file_name: file.name });
                } else {
                    const form = new FormData();
                    form.append("title", this.upload.title || "");
                    form.append("file", file);
                    await axios.post(this.uploadUrl, form);
                }
                this.upload = { open: false, title: "", file: null, loading: false, progress: null };
                this.$dialog.notify.success("Plano enviado.", { position: "top-right", timeout: 2500 });
                this.$emit("changed");
            } catch (error) {
                this.notifyError(error);
            } finally {
                this.upload.loading = false;
            }
        },

        async view(item) {
            this.busy = `view-${item.id}`;
            try {
                const result = await axios.get(this.fileUrl(item));
                window.open(result.data.content.url, "_blank");
            } catch (error) {
                this.notifyError(error);
            } finally {
                this.busy = null;
            }
        },

        async act(item, action, body = {}) {
            this.busy = `${action}-${item.id}`;
            try {
                await axios.post(this.actionUrl(item, action), body);
                this.$dialog.notify.success(action === "publish" || body.publish ? "Plano publicado." : "Plano atualizado.", { position: "top-right", timeout: 2500 });
                this.$emit("changed");
            } catch (error) {
                this.notifyError(error);
            } finally {
                this.busy = null;
            }
        },

        openReject(item) {
            this.reject = { open: true, reason: "", item };
        },

        async confirmReject() {
            this.busy = "reject";
            try {
                await axios.post(this.actionUrl(this.reject.item, "reject"), { reason: this.reject.reason.trim() });
                this.reject.open = false;
                this.$dialog.notify.success("Versão rejeitada.", { position: "top-right", timeout: 2500 });
                this.$emit("changed");
            } catch (error) {
                this.notifyError(error);
            } finally {
                this.busy = null;
            }
        },
    },
};
</script>
